import { Trans } from "@lingui/react/macro";
import { Link } from "react-router";
import {
  FileText,
  Globe,
  UserCheck,
  Music,
  GraduationCap,
  RefreshCcw,
  MessageSquare,
  ShieldAlert,
  XCircle,
  Edit3,
  Scale,
  FileCheck,
} from "lucide-react";
import LegalPageLayout, {
  LegalSectionConfig,
} from "../../components/legal/LegalPageLayout";
import LegalSection from "../../components/legal/LegalSection";
import CompanyContactInfo from "../../components/legal/CompanyContactInfo";

export default function Terms() {
  const sections: LegalSectionConfig[] = [
    {
      id: "scope",
      label: <Trans>1. Scope & General</Trans>,
      icon: FileText,
    },
    {
      id: "services",
      label: <Trans>2. Platform Services</Trans>,
      icon: Globe,
    },
    {
      id: "registration",
      label: <Trans>3. Account Registration</Trans>,
      icon: UserCheck,
    },
    {
      id: "teachers",
      label: <Trans>4. Rules for Teachers</Trans>,
      icon: Music,
    },
    {
      id: "students",
      label: <Trans>5. Booking & Payments</Trans>,
      icon: GraduationCap,
    },
    {
      id: "cancellation",
      label: <Trans>6. Cancellations</Trans>,
      icon: RefreshCcw,
    },
    {
      id: "content",
      label: <Trans>7. Code of Conduct</Trans>,
      icon: MessageSquare,
    },
    {
      id: "liability",
      label: <Trans>8. Liability</Trans>,
      icon: ShieldAlert,
    },
    {
      id: "termination",
      label: <Trans>9. Termination</Trans>,
      icon: XCircle,
    },
    {
      id: "changes",
      label: <Trans>10. Changes to Terms</Trans>,
      icon: Edit3,
    },
    {
      id: "governing",
      label: <Trans>11. Governing Law</Trans>,
      icon: Scale,
    },
    {
      id: "final",
      label: <Trans>12. Final Provisions</Trans>,
      icon: FileCheck,
    },
  ];

  return (
    <LegalPageLayout
      title={<Trans>Terms and Conditions</Trans>}
      description={
        <Trans>
          General Terms and Conditions for using the Soluud platform, governing
          the relationship between Soluud, teachers, and students.
        </Trans>
      }
      icon={FileText}
      sections={sections}
      tableOfContentsLabel={<Trans>Table of Contents</Trans>}
    >
      <LegalSection
        id="scope"
        title={<Trans>1. Scope & General Information</Trans>}
        icon={FileText}
      >
        <p>
          <Trans>
            These General Terms and Conditions (GTC) apply to all contracts
            concluded between Soluud (hereinafter "we", "us", or "Platform") and
            the users of our platform (hereinafter "Users", including "Teachers"
            and "Students").
          </Trans>
        </p>
        <p>
          <Trans>
            By registering on the platform or using our services, you agree to
            be bound by these terms. Deviating or conflicting terms of the User
            shall only apply if we have expressly agreed to them in writing.
          </Trans>
        </p>
        <p>
          <Trans>
            Information on how we process your personal data is set out in our{" "}
            <Link to="/privacy" className="text-soluud-primary hover:underline">
              Privacy Policy
            </Link>
            .
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="services"
        title={<Trans>2. Soluud's Role & Platform Services</Trans>}
        icon={Globe}
      >
        <p>
          <Trans>
            Soluud operates an online marketplace that connects independent
            music teachers with prospective students. We provide the technical
            infrastructure for searching, booking, scheduling, and payment
            processing.
          </Trans>
        </p>
        <p>
          <Trans>
            <strong>Important:</strong> Soluud is strictly an intermediary. We
            do not offer music lessons ourselves. Contracts for music lessons
            are exclusively concluded between the Student and the respective
            Teacher. We are not a party to the lesson contracts and assume no
            liability for the execution of the lessons.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="registration"
        title={<Trans>3. Account Registration & Eligibility</Trans>}
        icon={UserCheck}
      >
        <p>
          <Trans>
            Registration is required to use the booking and messaging features
            of the platform. Users must provide accurate, current, and complete
            information during the registration process.
          </Trans>
        </p>
        <p>
          <Trans>
            You must be at least 18 years old to create a Teacher account.
            Minors may use the platform as Students only with the involvement
            and consent of a parent or legal guardian.
          </Trans>
        </p>
        <p>
          <Trans>
            Users are responsible for keeping their login credentials secure and
            must not share their account with third parties.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="teachers"
        title={<Trans>4. Provisions for Teachers</Trans>}
        icon={Music}
      >
        <p>
          <Trans>
            Teachers offer their services as independent contractors. They are
            solely responsible for setting their own prices, schedules, and
            lesson formats, as well as declaring their income and paying any
            applicable taxes.
          </Trans>
        </p>
        <p>
          <Trans>
            To receive payouts, Teachers must complete the Stripe Connect
            onboarding process and comply with Stripe's Connected Account
            Agreement. Soluud retains a platform fee from successful bookings to
            cover operational and payment processing costs.
          </Trans>
        </p>
        <p>
          <Trans>
            Teachers are obligated to keep their profile information and
            availabilities up to date, and to conduct accepted lessons
            professionally and reliably.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="students"
        title={<Trans>5. Bookings, Subscriptions & Payments</Trans>}
        icon={GraduationCap}
      >
        <p>
          <Trans>
            Students can book individual lessons or recurring subscriptions with
            Teachers via the platform. By confirming a booking, a binding
            contract is formed between the Student and the Teacher.
          </Trans>
        </p>
        <p>
          <Trans>
            Payments are processed securely via Stripe. The Student agrees to
            provide valid payment information. In the case of subscriptions, the
            Student authorizes Soluud to charge the payment method on a
            recurring basis until the subscription is canceled.
          </Trans>
        </p>
        <p>
          <Trans>
            All prices are stated in euros and are the final amounts payable.
            Whether the price shown for a lesson includes statutory value-added
            tax (VAT) depends on the tax status of the respective Teacher as an
            independent contractor.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="cancellation"
        title={<Trans>6. Cancellations</Trans>}
        icon={RefreshCcw}
      >
        <p>
          <Trans>
            Lessons can be canceled by Students according to the platform's
            standard cancellation policy. The refund depends on how far in
            advance the cancellation is made relative to the scheduled lesson
            start time: a 90% refund if canceled more than four weeks before, a
            50% refund if canceled between two and four weeks before, and no
            refund (the lesson is no longer cancelable) within two weeks of the
            lesson start time.
          </Trans>
        </p>
        <p>
          <Trans>
            Currently, the cancellation of individual lessons from the Teacher's
            side is not supported by the platform.
          </Trans>
        </p>
        <p>
          <Trans>
            Subscriptions can be canceled by both Students and Teachers at any
            time before the next billing cycle via their respective dashboards.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="content"
        title={<Trans>7. User Content & Code of Conduct</Trans>}
        icon={MessageSquare}
      >
        <p>
          <Trans>
            Users may upload profile pictures, documents, and communicate via
            the built-in chat. You grant Soluud a non-exclusive right to host
            and display this content to operate the platform.
          </Trans>
        </p>
        <p>
          <Trans>Users strictly agree not to:</Trans>
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>
            <Trans>Upload copyrighted material without authorization.</Trans>
          </li>
          <li>
            <Trans>
              Engage in abusive, harassing, or discriminatory behavior in the
              chat or during lessons.
            </Trans>
          </li>
          <li>
            <Trans>
              Use the platform to bypass the Soluud booking system and arrange
              payments externally for initially connected students.
            </Trans>
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        id="liability"
        title={<Trans>8. Limitation of Liability</Trans>}
        icon={ShieldAlert}
      >
        <p>
          <Trans>
            Soluud is liable without limitation for damages caused intentionally
            or by gross negligence. For slight negligence, we are only liable
            for breaches of essential contractual obligations (cardinal
            obligations), limited to typical, foreseeable damages.
          </Trans>
        </p>
        <p>
          <Trans>
            The above limitations of liability do not apply to liability for
            damages resulting from injury to life, body, or health, to mandatory
            liability under the German Product Liability Act
            (Produkthaftungsgesetz), to fraudulently concealed defects, or where
            we have assumed a guarantee.
          </Trans>
        </p>
        <p>
          <Trans>
            We assume no liability for the quality, safety, or legality of the
            music lessons provided by Teachers. Any claims regarding the lessons
            must be directed entirely to the respective Teacher.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="termination"
        title={<Trans>9. Term & Termination</Trans>}
        icon={XCircle}
      >
        <p>
          <Trans>
            The usage contract with Soluud is concluded for an indefinite
            period. Users may terminate their account at any time. Any pending
            bookings or subscriptions must be completed or properly canceled
            before account deletion.
          </Trans>
        </p>
        <p>
          <Trans>
            We reserve the right to temporarily suspend or permanently terminate
            user accounts in cases of severe violations of these Terms (e.g.,
            harassment, fraud, bypassing payment).
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="changes"
        title={<Trans>10. Changes to the Terms</Trans>}
        icon={Edit3}
      >
        <p>
          <Trans>
            We may amend these Terms where there is a valid reason to do so, for
            example to reflect changes to our services, to the legal situation,
            or to technical conditions. We will notify users of any changes via
            email at least 30 days before they take effect. The notification
            will highlight the changes and inform users of their right to object
            and of the consequences of remaining silent.
          </Trans>
        </p>
        <p>
          <Trans>
            If a user does not object before the changes take effect, this is
            deemed acceptance of the changes. This deemed acceptance does not
            apply to changes affecting the essential rights and obligations of
            the contract (in particular price or the scope of services), which
            require the user's express consent. Users may terminate the contract
            at any time, and statutory rights to object remain unaffected.
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="governing"
        title={<Trans>11. Governing Law & Dispute Resolution</Trans>}
        icon={Scale}
      >
        <p>
          <Trans>
            These Terms are governed by the laws of the Federal Republic of
            Germany, excluding the UN Convention on Contracts for the
            International Sale of Goods (CISG).
          </Trans>
        </p>
        <p>
          <Trans>
            If the User is a merchant, a legal entity under public law, or a
            special fund under public law, the exclusive place of jurisdiction
            for all disputes arising from this contract is the registered office
            of Soluud.
          </Trans>
        </p>
        <p>
          <Trans>
            We are neither obliged nor willing to participate in a dispute
            resolution procedure before a consumer arbitration board
            (Verbraucherschlichtungsstelle).
          </Trans>
        </p>
        <p>
          <Trans>
            The European Commission provides a platform for online dispute
            resolution (ODR), which can be found at{" "}
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-soluud-primary hover:underline"
            >
              https://ec.europa.eu/consumers/odr
            </a>
            .
          </Trans>
        </p>
      </LegalSection>

      <LegalSection
        id="final"
        title={<Trans>12. Final Provisions</Trans>}
        icon={FileCheck}
      >
        <p>
          <Trans>
            Should individual provisions of these Terms be or become invalid or
            unenforceable, the validity of the remaining provisions shall remain
            unaffected.
          </Trans>
        </p>
        <p>
          <Trans>
            These Terms are made available in English and German. For consumers
            whose habitual residence is in Germany, the German version is
            legally binding.
          </Trans>
        </p>

        <div className="mt-8">
          <CompanyContactInfo />
        </div>
      </LegalSection>
    </LegalPageLayout>
  );
}
